import { useState, useEffect } from 'react';
import axios from 'axios';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Plus, Trash, PencilSimple, ArrowUp, ArrowDown, X, Check, Image as ImageIcon, UploadSimple, ListDashes, SlidersHorizontal } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Global auto-sort toggle for the homepage Categories grid — lives in
   platform_config (same singleton doc as maintenance_mode etc). Does NOT
   touch the existing manual drag-order (ArrowUp/ArrowDown) mechanism below;
   it's purely an opt-in override consumed by LandingPage.js. */
const CategoryAutoSortToggle = () => {
  const { t } = useTranslation();
  const [enabled, setEnabled] = useState(false);
  const [threshold, setThreshold] = useState(10);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/platform-config`, { withCredentials: true })
      .then(({ data }) => {
        setEnabled(!!data.category_auto_sort_enabled);
        setThreshold(data.category_auto_sort_threshold ?? 10);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/platform-config`, patch, { withCredentials: true });
      setEnabled(!!data.category_auto_sort_enabled);
      setThreshold(data.category_auto_sort_threshold ?? 10);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save auto-sort settings');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (checked) => { setEnabled(checked); save({ category_auto_sort_enabled: checked }); };
  const handleThresholdBlur = () => {
    const val = Math.max(0, parseInt(threshold, 10) || 0);
    setThreshold(val);
    save({ category_auto_sort_threshold: val });
  };

  if (loading) return null;

  return (
    <div className="bg-surface border border-border rounded-lg p-4 flex items-start justify-between gap-4 mb-4" data-testid="category-auto-sort-card">
      <div>
        <p className="text-sm font-semibold text-text_main">{t('admin.autoSort')}</p>
        <p className="text-xs text-text_muted mt-0.5 max-w-md">
          When ON, categories with active listing count above the threshold are promoted to the top of the homepage grid automatically. Categories below it keep their manual position.
        </p>
        {enabled && (
          <div className="mt-3 flex items-center gap-2">
            <Label className="text-xs text-text_muted whitespace-nowrap">{t('admin.autoSortThreshold')}</Label>
            <Input
              type="number"
              min={0}
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
              onBlur={handleThresholdBlur}
              className="w-20 h-8 text-sm"
              data-testid="category-auto-sort-threshold-input"
            />
          </div>
        )}
      </div>
      <Switch
        checked={enabled}
        onCheckedChange={handleToggle}
        disabled={saving}
        data-testid="category-auto-sort-toggle"
      />
    </div>
  );
};

export const GlobalStructureSection = ({ renderFieldsTab }) => {
  const [tab, setTab] = useState('categories');

  return (
    <div className="space-y-6" data-testid="global-structure-section">
      <h2 className="text-lg font-medium text-text_strong tracking-wide">Global Structure</h2>
      <p className="text-sm text-text_muted">Manage categories, subcategories and their custom fields. Changes apply platform-wide.</p>

      <div className="flex gap-2 border-b border-border pb-0">
        <button onClick={() => setTab('categories')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${tab === 'categories' ? 'border-primary text-primary' : 'border-transparent text-text_muted hover:text-text_main'}`}
          data-testid="tab-categories">
          <ListDashes size={16} className="inline mr-1.5 -mt-0.5" /> Categories Custom Fields
        </button>
        <button onClick={() => setTab('subcategory_fields')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${tab === 'subcategory_fields' ? 'border-primary text-primary' : 'border-transparent text-text_muted hover:text-text_main'}`}
          data-testid="tab-subcategory-fields">
          <SlidersHorizontal size={16} className="inline mr-1.5 -mt-0.5" /> Sub Categories Custom Fields
        </button>
      </div>

      {tab === 'categories' && <CategoriesManager />}
      {tab === 'subcategory_fields' && (renderFieldsTab ? renderFieldsTab() : null)}
    </div>
  );
};

/* ─── Categories Manager (matches Regions & Cities UX) ─── */
const CategoriesManager = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCat, setExpandedCat] = useState(null);
  const [editingKey, setEditingKey] = useState(null);
  const [editName, setEditName] = useState('');
  const [showAddSub, setShowAddSub] = useState(null);
  const [newSubValue, setNewSubValue] = useState('');
  const [newSubName, setNewSubName] = useState('');
  const [showAddCat, setShowAddCat] = useState(false);
  const [newCatKey, setNewCatKey] = useState('');
  const [newCatName, setNewCatName] = useState('');
  const [editingSub, setEditingSub] = useState(null); // { catKey, value }
  const [editSubName, setEditSubName] = useState('');
  const [editSubValue, setEditSubValue] = useState('');
  const [editSubDescription, setEditSubDescription] = useState('');
  const [newSubDescription, setNewSubDescription] = useState('');

  const fetchCategories = () => {
    setLoading(true);
    axios.get(`${API}/admin/structure/categories`, { withCredentials: true })
      .then(res => setCategories(res.data))
      .catch(() => toast.error('Failed to load categories'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchCategories(); }, []);

  const handleRenameSave = async (key) => {
    if (!editName.trim()) return;
    try {
      const cat = categories.find(c => c.key === key);
      await axios.put(`${API}/admin/structure/categories/${key}`, { key, name: editName, subcategories: cat.subcategories || [], icon: cat.icon }, { withCredentials: true });
      toast.success('Category renamed');
      setEditingKey(null);
      fetchCategories();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (key) => {
    if (!window.confirm(`Delete category "${key}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/structure/categories/${key}`, { withCredentials: true });
      toast.success('Category deleted');
      fetchCategories();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleAddSub = async (catKey) => {
    if (!newSubValue.trim() || !newSubName.trim()) return;
    try {
      await axios.post(`${API}/admin/structure/categories/${catKey}/subcategories`, {
        value: newSubValue.toLowerCase().replace(/[^a-z0-9_]/g, ''),
        name: newSubName,
        description: newSubDescription.trim() || null,
      }, { withCredentials: true });
      toast.success('Subcategory created');
      setShowAddSub(null); setNewSubValue(''); setNewSubName(''); setNewSubDescription('');
      fetchCategories();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteSub = async (catKey, subValue) => {
    try {
      await axios.delete(`${API}/admin/structure/categories/${catKey}/subcategories/${subValue}`, { withCredentials: true });
      toast.success('Subcategory deleted');
      fetchCategories();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleAddCategory = async () => {
    if (!newCatKey.trim() || !newCatName.trim()) return;
    const key = newCatKey.toLowerCase().replace(/[^a-z0-9_]/g, '');
    try {
      await axios.put(`${API}/admin/structure/categories/${key}`, { key, name: newCatName, subcategories: [] }, { withCredentials: true });
      toast.success('Category created');
      setShowAddCat(false); setNewCatKey(''); setNewCatName('');
      fetchCategories();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleMove = async (idx, dir) => {
    const newOrder = [...categories];
    const target = idx + dir;
    if (target < 0 || target >= newOrder.length) return;
    [newOrder[idx], newOrder[target]] = [newOrder[target], newOrder[idx]];
    setCategories(newOrder);
    try {
      await axios.put(`${API}/admin/structure/categories-order`, newOrder.map(c => c.key), { withCredentials: true });
    } catch { toast.error('Reorder failed'); }
  };

  const handleToggleCat = async (key) => {
    try {
      const { data } = await axios.put(`${API}/admin/structure/categories/${key}/toggle-active`, {}, { withCredentials: true });
      setCategories(prev => prev.map(c => c.key === key ? { ...c, active: data.active } : c));
      toast.success(data.message);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleToggleSub = async (catKey, subValue) => {
    try {
      const { data } = await axios.put(`${API}/admin/structure/categories/${catKey}/subcategories/${subValue}/toggle-active`, {}, { withCredentials: true });
      setCategories(prev => prev.map(c => {
        if (c.key !== catKey) return c;
        return { ...c, subcategories: c.subcategories.map(s => s.value === subValue ? { ...s, active: data.active } : s) };
      }));
      toast.success(data.message);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleEditSubSave = async (catKey, oldValue) => {
    if (!editSubName.trim()) return;
    try {
      await axios.put(`${API}/admin/structure/categories/${catKey}/subcategories/${oldValue}`, {
        value: editSubValue,
        name: editSubName,
        description: editSubDescription.trim() || null,
      }, { withCredentials: true });
      toast.success('Subcategory saved');
      setEditingSub(null);
      fetchCategories();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleMoveSub = async (catKey, idx, dir) => {
    const cat = categories.find(c => c.key === catKey);
    if (!cat) return;
    const subs = [...cat.subcategories];
    const target = idx + dir;
    if (target < 0 || target >= subs.length) return;
    [subs[idx], subs[target]] = [subs[target], subs[idx]];
    setCategories(prev => prev.map(c => c.key === catKey ? { ...c, subcategories: subs } : c));
    try {
      await axios.put(`${API}/admin/structure/categories/${catKey}/subcategories-order`, subs.map(s => s.value), { withCredentials: true });
    } catch { toast.error('Reorder failed'); }
  };

  const handleIconUpload = async (catKey, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['png', 'svg'].includes(ext)) {
      toast.error('Only SVG and PNG formats are allowed.');
      e.target.value = ''; return;
    }
    if (file.size > 100 * 1024) {
      toast.error('File size exceeds 100KB limit.');
      e.target.value = ''; return;
    }
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/admin/structure/categories/${catKey}/icon`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Icon uploaded');
      fetchCategories();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Icon upload failed');
    }
    e.target.value = '';
  };

  const handleIconRemove = async (catKey) => {
    try {
      await axios.delete(`${API}/admin/structure/categories/${catKey}/icon`, { withCredentials: true });
      toast.success('Icon removed');
      fetchCategories();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div className="py-8 text-center text-text_muted">Loading...</div>;

  return (
    <div className="space-y-3" data-testid="categories-manager">
      <CategoryAutoSortToggle />
      <div className="flex justify-between items-center">
        <span className="text-sm text-text_muted">{categories.length} categories, {categories.reduce((a, c) => a + (c.subcategories?.length || 0), 0)} subcategories total</span>
        <Button size="sm" onClick={() => setShowAddCat(true)} className="bg-primary hover:bg-primary-hover text-white gap-1.5" data-testid="add-category-btn">
          <Plus size={16} weight="bold" /> Add Category
        </Button>
      </div>

      {showAddCat && (
        <div className="bg-surface border border-border rounded-lg p-4 flex gap-3 items-end" data-testid="add-category-form">
          <div className="flex-1">
            <Label className="text-xs text-text_muted mb-1 block">Key (slug)</Label>
            <Input value={newCatKey} onChange={(e) => setNewCatKey(e.target.value)} placeholder="e.g. furniture" className="border border-border text-sm" />
          </div>
          <div className="flex-1">
            <Label className="text-xs text-text_muted mb-1 block">Display Name</Label>
            <Input value={newCatName} onChange={(e) => setNewCatName(e.target.value)} placeholder="e.g. Furniture" className="border border-border text-sm" />
          </div>
          <Button size="sm" variant="success" onClick={handleAddCategory}><Check size={16} /></Button>
          <Button size="sm" variant="outline" onClick={() => setShowAddCat(false)}><X size={16} /></Button>
        </div>
      )}

      {categories.map((cat, idx) => (
        <div key={cat.key} className={`bg-white border border-border rounded-lg overflow-hidden ${cat.active === false ? 'opacity-60' : ''}`} data-testid={`cat-${cat.key}`}>
          {/* Collapsed header — identical to Region header */}
          <div className="flex items-center gap-3 p-4 cursor-pointer hover:bg-surface/50" onClick={() => setExpandedCat(expandedCat === cat.key ? null : cat.key)}>
            <div className="flex flex-col gap-0.5" onClick={(e) => e.stopPropagation()}>
              <button onClick={() => handleMove(idx, -1)} disabled={idx === 0} className="text-text_muted hover:text-text_main disabled:opacity-30"><ArrowUp size={12} /></button>
              <button onClick={() => handleMove(idx, 1)} disabled={idx === categories.length - 1} className="text-text_muted hover:text-text_main disabled:opacity-30"><ArrowDown size={12} /></button>
            </div>
            <ListDashes size={18} className="text-primary" />
            {cat.icon_url && (
              <img src={`${API}/files/${cat.icon_url}`} alt="" className="w-6 h-6 object-contain rounded" />
            )}
            {editingKey === cat.key ? (
              <div className="flex gap-2 items-center flex-1" onClick={(e) => e.stopPropagation()}>
                <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="border border-border text-sm max-w-xs" autoFocus
                  onKeyDown={(e) => e.key === 'Enter' && handleRenameSave(cat.key)} />
                <Button size="sm" variant="success" onClick={() => handleRenameSave(cat.key)} className="h-8 px-2"><Check size={14} /></Button>
                <Button size="sm" variant="outline" onClick={() => setEditingKey(null)} className="h-8 px-2"><X size={14} /></Button>
              </div>
            ) : (
              <span className="font-medium text-sm flex-1">{cat.name}</span>
            )}
            <span className="text-xs bg-surface text-text_muted px-2 py-0.5 rounded">{cat.subcategories?.length || 0} subcategories</span>
            <button
              onClick={(e) => { e.stopPropagation(); handleToggleCat(cat.key); }}
              className={`text-xs font-medium px-2.5 py-1 rounded-full transition-colors ${cat.active !== false ? 'bg-success-soft text-success hover:bg-success-soft' : 'bg-danger-soft text-danger hover:bg-danger-soft'}`}
              data-testid={`toggle-cat-${cat.key}`}
            >
              {cat.active !== false ? 'ON' : 'OFF'}
            </button>
            <button onClick={(e) => { e.stopPropagation(); setEditingKey(cat.key); setEditName(cat.name); }} className="text-text_muted hover:text-primary"><PencilSimple size={16} /></button>
            <button onClick={(e) => { e.stopPropagation(); handleDelete(cat.key); }} className="text-text_muted hover:text-danger"><Trash size={16} /></button>
          </div>

          {/* Expanded: Icon management + Subcategories */}
          {expandedCat === cat.key && (
            <div className="border-t border-border p-4 pl-14 bg-surface/30 space-y-3">
              {/* Icon Management */}
              <div className="flex items-center gap-4 pb-3 border-b border-border/50" data-testid={`icon-manager-${cat.key}`}>
                <div className="w-16 h-16 rounded-lg border-2 border-dashed border-border flex items-center justify-center bg-white overflow-hidden">
                  {cat.icon_url ? (
                    <img src={`${API}/files/${cat.icon_url}`} alt={cat.name} className="w-14 h-14 object-contain" />
                  ) : (
                    <ImageIcon size={28} className="text-text_muted" />
                  )}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-text_main mb-1">Category Icon</p>
                  <p className="text-xs text-text_muted mb-2">PNG or SVG, max 256x256px, max 100KB</p>
                  <div className="flex gap-2">
                    <label className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-primary/10 text-primary rounded-lg cursor-pointer hover:bg-primary/20 transition-colors" data-testid={`upload-icon-${cat.key}`}>
                      <UploadSimple size={14} weight="bold" /> {cat.icon_url ? 'Replace' : 'Upload'}
                      <input type="file" accept=".png,.svg,image/png,image/svg+xml" onChange={(e) => handleIconUpload(cat.key, e)} className="hidden" />
                    </label>
                    {cat.icon_url && (
                      <button onClick={() => handleIconRemove(cat.key)} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-danger bg-danger-soft rounded-lg hover:bg-danger-soft transition-colors" data-testid={`remove-icon-${cat.key}`}>
                        <Trash size={14} /> Remove
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Subcategories */}
              {cat.subcategories?.map((sub, subIdx) => {
                const isEditingThis = editingSub?.catKey === cat.key && editingSub?.value === sub.value;
                if (isEditingThis) {
                  return (
                    <div key={sub.value} className="p-3 bg-white border border-primary/40 rounded-lg space-y-2" data-testid={`edit-sub-form-${sub.value}`}>
                      <div className="flex gap-2 items-center">
                        <Input value={editSubName} onChange={(e) => setEditSubName(e.target.value)} className="border border-border text-xs h-7 w-32" autoFocus
                          placeholder="Name" onKeyDown={(e) => e.key === 'Enter' && handleEditSubSave(cat.key, sub.value)} />
                        <Input value={editSubValue} onChange={(e) => setEditSubValue(e.target.value)} className="border border-border text-xs h-7 w-24 font-mono" placeholder="slug" />
                        <button
                          onClick={() => handleToggleSub(cat.key, sub.value)}
                          className={`text-[10px] font-medium px-2 py-0.5 rounded-full transition-colors ${sub.active !== false ? 'bg-success-soft text-success hover:bg-success-soft' : 'bg-danger-soft text-danger hover:bg-danger-soft'}`}
                          data-testid={`toggle-sub-${sub.value}`}
                        >
                          {sub.active !== false ? 'ON' : 'OFF'}
                        </button>
                      </div>
                      <div>
                        <Label className="text-[11px] text-text_muted mb-1 block">Description / Hint for users 🆕</Label>
                        <textarea
                          value={editSubDescription}
                          onChange={(e) => { if (e.target.value.length <= 150) setEditSubDescription(e.target.value); }}
                          placeholder="e.g., Cleaning, plumbing, electrical, painting, and other household maintenance services"
                          rows={3}
                          maxLength={150}
                          className="w-full max-h-[60px] resize-y border border-border rounded-md text-xs p-2"
                          data-testid={`edit-sub-description-${sub.value}`}
                        />
                        <div className="text-right text-[11px] text-text_muted mt-0.5">{editSubDescription.length} / 150 characters</div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="success" onClick={() => handleEditSubSave(cat.key, sub.value)} className="h-7 px-3" data-testid={`save-sub-btn-${sub.value}`}>Save changes</Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingSub(null)} className="h-7 px-3" data-testid={`cancel-sub-edit-${sub.value}`}>Cancel</Button>
                      </div>
                    </div>
                  );
                }
                return (
                  <div key={sub.value} className={`flex items-center gap-2 text-sm py-1.5 px-2 rounded hover:bg-white group ${sub.active === false ? 'opacity-50' : ''}`} data-testid={`sub-${sub.value}`}>
                    <div className="flex flex-col gap-0.5" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => handleMoveSub(cat.key, subIdx, -1)} disabled={subIdx === 0} className="text-text_muted hover:text-text_main disabled:opacity-30"><ArrowUp size={10} /></button>
                      <button onClick={() => handleMoveSub(cat.key, subIdx, 1)} disabled={subIdx === (cat.subcategories?.length || 0) - 1} className="text-text_muted hover:text-text_main disabled:opacity-30"><ArrowDown size={10} /></button>
                    </div>
                    <span className="text-text_main">{sub.name}</span>
                    {sub.description && (
                      <span
                        className="w-4 h-4 rounded-full border border-primary text-primary text-[10px] font-bold flex items-center justify-center cursor-help shrink-0"
                        title={sub.description}
                        data-testid={`sub-desc-indicator-${sub.value}`}
                      >i</span>
                    )}
                    <span className="text-xs text-text_muted font-mono">{sub.value}</span>
                    <div className="ml-auto flex items-center gap-2">
                      <button
                        onClick={() => handleToggleSub(cat.key, sub.value)}
                        className={`text-[10px] font-medium px-2 py-0.5 rounded-full transition-colors ${sub.active !== false ? 'bg-success-soft text-success hover:bg-success-soft' : 'bg-danger-soft text-danger hover:bg-danger-soft'}`}
                        data-testid={`toggle-sub-${sub.value}`}
                      >
                        {sub.active !== false ? 'ON' : 'OFF'}
                      </button>
                      <button onClick={() => { setEditingSub({ catKey: cat.key, value: sub.value }); setEditSubName(sub.name); setEditSubValue(sub.value); setEditSubDescription(sub.description || ''); }} className="text-text_muted hover:text-primary opacity-0 group-hover:opacity-100" data-testid={`edit-sub-btn-${sub.value}`}><PencilSimple size={14} /></button>
                      <button onClick={() => handleDeleteSub(cat.key, sub.value)} className="text-text_muted hover:text-danger opacity-0 group-hover:opacity-100" data-testid={`delete-sub-btn-${sub.value}`}><Trash size={14} /></button>
                    </div>
                  </div>
                );
              })}
              {showAddSub === cat.key ? (
                <div className="p-3 bg-white border border-border rounded-lg space-y-2 mt-2" data-testid={`add-sub-form-${cat.key}`}>
                  <div className="flex gap-2 items-center">
                    <Input value={newSubValue} onChange={(e) => setNewSubValue(e.target.value)} placeholder="Key (e.g. sedan)" className="border border-border text-xs h-8 w-28" autoFocus />
                    <Input value={newSubName} onChange={(e) => setNewSubName(e.target.value)} placeholder="Name (e.g. Sedan)" className="border border-border text-xs h-8 w-32" />
                  </div>
                  <div>
                    <Label className="text-[11px] text-text_muted mb-1 block">Description / Hint for users 🆕</Label>
                    <textarea
                      value={newSubDescription}
                      onChange={(e) => { if (e.target.value.length <= 150) setNewSubDescription(e.target.value); }}
                      placeholder="e.g., Cleaning, plumbing, electrical, painting, and other household maintenance services"
                      rows={3}
                      maxLength={150}
                      className="w-full max-h-[60px] resize-y border border-border rounded-md text-xs p-2"
                      data-testid={`add-sub-description-${cat.key}`}
                    />
                    <div className="text-right text-[11px] text-text_muted mt-0.5">{newSubDescription.length} / 150 characters</div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="success" onClick={() => handleAddSub(cat.key)} className="h-8 px-3" data-testid={`create-subcategory-btn-${cat.key}`}>Create subcategory</Button>
                    <Button size="sm" variant="outline" onClick={() => { setShowAddSub(null); setNewSubDescription(''); }} className="h-8 px-3" data-testid={`cancel-add-sub-${cat.key}`}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <button onClick={() => { setShowAddSub(cat.key); setNewSubValue(''); setNewSubName(''); setNewSubDescription(''); }} className="text-xs text-primary hover:underline flex items-center gap-1 mt-2" data-testid={`add-sub-trigger-${cat.key}`}>
                  <Plus size={12} /> Add subcategory
                </button>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
